/**
 * /wireline-slickline/jobs/new — create a job (POST /wireline-slickline/jobs,
 * fieldiq-api#1338). A full page, not a modal; on save the job's own page
 * replaces this one so Back lands on the register.
 *
 * WL_DISPATCH only (admin, office_staff, supervisor, dispatcher). Any other
 * role is told so here rather than handed a form the API would refuse.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useWlMeta, useWlUsers, useWlWells } from '../../hooks/useWirelineSlickline';
import { useCreateWlJob } from '../../hooks/useWlJobs';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { wlWellIdOf } from '../../lib/wireline-slickline';
import { canDispatchWl, wlJobRefusal } from '../../lib/wireline-slickline-jobs';
import { useBackToList } from '../downhole/recordPage';
import { JobForm } from './JobForm';
import { WL_EYEBROW, WlEmpty, WlLoading, WlPanel } from './WlBits';

export default function WlNewJobScreen() {
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/jobs');
  const { user } = useAuth();
  const canAdd = canDispatchWl(user?.role);
  const meta = useWlMeta();
  const wells = useWlWells();
  const users = useWlUsers(canAdd);
  const create = useCreateWlJob();
  const [error, setError] = useState<string | null>(null);
  const failed = [meta, wells, users].find((q) => queryLoadFailure(q));
  const country = wells.data?.meta.country ?? null;

  return (
    <div data-testid="wl-new-job-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand eyebrow={WL_EYEBROW} title="New job" onBack={back} backLabel="Back" />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <WlPanel eyebrow="Job register" title="Job">
            {!canAdd ? (
              <WlEmpty>Only an admin, office staff, a supervisor or a dispatcher can create a job.</WlEmpty>
            ) : meta.isLoading || wells.isLoading || users.isLoading ? (
              <WlLoading label="Loading form" />
            ) : failed ? (
              <LoadFailureNotice error={queryFailureError(failed)} kind={queryLoadFailure(failed) || 'unreachable'} subject="the job form" />
            ) : (wells.data?.data ?? []).length === 0 ? (
              <WlEmpty>A job is on a well, and there are no wells yet. Add one under Wells first.</WlEmpty>
            ) : (
              <JobForm
                job={null}
                catalog={meta.data?.data ?? null}
                wells={(wells.data?.data ?? []).map((w) => ({
                  id: w.id,
                  wellName: w.wellName,
                  customerName: w.customerName,
                  idText: wlWellIdOf(w, { country }),
                }))}
                users={users.data ?? []}
                pending={create.isPending}
                error={error}
                submitLabel="Create job"
                allowScheduled
                onCancel={back}
                onSubmit={(body) => {
                  setError(null);
                  create.mutate(body, {
                    onSuccess: (res) => {
                      toast.success(res.data?.jobNumber ? `Job ${res.data.jobNumber} created` : 'Job created');
                      navigate(res.data?.id ? `/wireline-slickline/jobs/${res.data.id}` : '/wireline-slickline/jobs', { replace: true });
                    },
                    onError: (err) => setError(wlJobRefusal(err, 'The job could not be created.')),
                  });
                }}
              />
            )}
          </WlPanel>
        </div>
      </section>
    </div>
  );
}
